// Local Authentication Service - Mock authentication without Supabase dependency
const LOCAL_STORAGE_KEY = 'laborpro_local_auth';

// Demo user credentials and profile mapping
const DEMO_USERS = {
  'admin@laborpro.com': {
    password: 'Admin@123',
    profile: {
      id: 'local-admin',
      email: 'admin@laborpro.com',
      full_name: 'Super Admin',
      role: 'super_admin',
      phone: '+91-9876543201',
      department: 'Administration',
      permissions: ['all'],
      dashboardRoute: '/erp/dashboard-overview'
    }
  },
  'hr@laborpro.com': {
    password: 'HR@123',
    profile: {
      id: 'local-hr',
      email: 'hr@laborpro.com',
      full_name: 'HR Manager',
      role: 'hr_admin',
      phone: '+91-9876543202',
      department: 'Human Resources',
      permissions: ['employee_management', 'payroll_management', 'applicant_tracking'],
      dashboardRoute: '/erp/employee-management'
    }
  },
  'telecaller@laborpro.com': {
    password: 'Telecaller@123',
    profile: {
      id: 'local-telecaller',
      email: 'telecaller@laborpro.com',
      full_name: 'Telecaller User',
      role: 'telecaller',
      phone: '+91-9876543203',
      department: 'Sales & Marketing',
      permissions: ['crm_lead_management', 'telecaller_dashboard'],
      dashboardRoute: '/erp/telecaller-dashboard'
    }
  },
  'staff@laborpro.com': {
    password: 'Staff@123',
    profile: {
      id: 'local-staff',
      email: 'staff@laborpro.com',
      full_name: 'Staff User',
      role: 'staff',
      phone: '+91-9876543204',
      department: 'Operations',
      permissions: ['dashboard_overview', 'client_requirements'],
      dashboardRoute: '/erp/client-requirements-matching'
    }
  },
  'client@laborpro.com': {
    password: 'Client@123',
    profile: {
      id: 'local-client',
      email: 'client@laborpro.com',
      full_name: 'Client User',
      role: 'client',
      phone: '+91-9876543205',
      department: 'External Client',
      permissions: ['invoice_billing', 'client_requirements'],
      dashboardRoute: '/erp/invoice-billing-management'
    }
  },
  'employee@laborpro.com': {
    password: 'Employee@123',
    profile: {
      id: 'local-employee',
      email: 'employee@laborpro.com',
      full_name: 'Employee User',
      role: 'employee',
      phone: '+91-9876543206',
      department: 'Operations',
      permissions: ['self_service'],
      dashboardRoute: '/erp/employee-self-service'
    }
  },
  // Legacy credentials for backward compatibility
  'admin@facilitypro.com': {
    password: 'admin123',
    profile: {
      id: 'local-legacy-admin',
      email: 'admin@facilitypro.com',
      full_name: 'Legacy Admin',
      role: 'super_admin',
      phone: '+91-9876543206',
      department: 'Administration',
      permissions: ['all'],
      dashboardRoute: '/erp/dashboard-overview'
    }
  },
  'hr@facilitypro.com': {
    password: 'hr123',
    profile: {
      id: 'local-legacy-hr',
      email: 'hr@facilitypro.com',
      full_name: 'Legacy HR',
      role: 'hr_admin',
      phone: '+91-9876543207',
      department: 'Human Resources',
      permissions: ['employee_management', 'payroll_management'],
      dashboardRoute: '/erp/employee-management'
    }
  },
  'employee1@facilitypro.com': {
    password: 'emp123',
    profile: {
      id: 'local-legacy-emp1',
      email: 'employee1@facilitypro.com',
      full_name: 'Legacy Employee 1',
      role: 'employee',
      phone: '+91-9876543208',
      department: 'General',
      permissions: ['dashboard_overview'],
      dashboardRoute: '/erp/employee-self-service'
    }
  },
  'employee2@facilitypro.com': {
    password: 'emp123',
    profile: {
      id: 'local-legacy-emp2',
      email: 'employee2@facilitypro.com',
      full_name: 'Legacy Employee 2',
      role: 'employee',
      phone: '+91-9876543209',
      department: 'General',
      permissions: ['dashboard_overview'],
      dashboardRoute: '/erp/employee-self-service'
    }
  }
};

// Create mock user session
const createUserSession = (userProfile) => {
  const session = {
    user: {
      id: userProfile.id,
      email: userProfile.email,
      app_metadata: {},
      user_metadata: {
        full_name: userProfile.full_name,
        role: userProfile.role
      },
      aud: 'authenticated',
      created_at: new Date().toISOString()
    },
    access_token: `local_token_${userProfile.id}_${Date.now()}`,
    refresh_token: `local_refresh_${userProfile.id}_${Date.now()}`,
    expires_at: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
    token_type: 'bearer'
  };

  return session;
};

// Store session in localStorage
const storeSession = (session, userProfile) => {
  const authData = {
    session,
    userProfile,
    timestamp: Date.now(),
    expiresAt: session.expires_at
  };
  
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(authData));
};

// Retrieve session from localStorage
const getStoredSession = () => {
  try {
    const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!storedData) return null;

    const authData = JSON.parse(storedData);
    
    // Check if session has expired
    if (Date.now() > authData.expiresAt) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      return null;
    }

    return authData;
  } catch (error) {
    console.log('Error retrieving stored session:', error);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    return null;
  }
};

// Clear session from localStorage
const clearStoredSession = () => {
  localStorage.removeItem(LOCAL_STORAGE_KEY);
};

// Local sign in function
const localSignIn = async (email, password) => {
  try {
    // Simulate network delay for realistic experience
    await new Promise(resolve => setTimeout(resolve, 500));

    // Validate input
    if (!email || !password) {
      return {
        success: false,
        error: 'Email and password are required'
      };
    }

    // Normalize email
    const normalizedEmail = email.trim().toLowerCase();

    // Check credentials
    const userCredentials = DEMO_USERS[normalizedEmail];
    if (!userCredentials) {
      return {
        success: false,
        error: 'Invalid email or password. Please check your credentials and try again.'
      };
    }

    if (userCredentials.password !== password) {
      return {
        success: false,
        error: 'Invalid email or password. Please check your credentials and try again.'
      };
    }

    // Create session
    const userProfile = userCredentials.profile;
    const session = createUserSession(userProfile);

    // Store session
    storeSession(session, userProfile);

    console.log(`✅ Local authentication successful for: ${normalizedEmail} (${userProfile.role})`);
    
    return {
      success: true,
      data: {
        user: session.user,
        session: session
      },
      userProfile: userProfile,
      dashboardRoute: userProfile.dashboardRoute,
      isLocalAuth: true
    };

  } catch (error) {
    console.log('Local authentication error:', error);
    return {
      success: false,
      error: 'Authentication service temporarily unavailable. Please try again.'
    };
  }
};

// Local sign out function
const localSignOut = async () => {
  try {
    clearStoredSession();
    console.log('✅ Local sign out successful');
    return { success: true };
  } catch (error) {
    console.log('Local sign out error:', error);
    return { success: false, error: 'Sign out failed. Please try again.' };
  }
};

// Get current local session
const getLocalSession = async () => {
  try {
    const storedSession = getStoredSession();
    
    if (!storedSession) {
      return { success: true, data: { session: null } };
    }

    return {
      success: true,
      data: {
        session: storedSession.session
      }
    };
  } catch (error) {
    console.log('Get local session error:', error);
    return { success: false, error: 'Failed to get session.' };
  }
};

// Get user profile
const getLocalUserProfile = async (userId) => {
  try {
    const storedSession = getStoredSession();
    
    if (!storedSession || storedSession.session?.user?.id !== userId) {
      return { success: false, error: 'User profile not found.' };
    }

    return {
      success: true,
      data: storedSession.userProfile
    };
  } catch (error) {
    console.log('Get local user profile error:', error);
    return { success: false, error: 'Failed to fetch user profile.' };
  }
};

// Update user profile
const updateLocalUserProfile = async (userId, updates) => {
  try {
    const storedSession = getStoredSession();
    
    if (!storedSession || storedSession.session?.user?.id !== userId) {
      return { success: false, error: 'User not found.' };
    }

    // Update profile
    const updatedProfile = {
      ...storedSession.userProfile,
      ...updates,
      updated_at: new Date().toISOString()
    };

    // Update stored session
    storeSession(storedSession.session, updatedProfile);

    return { success: true, data: updatedProfile };
  } catch (error) {
    console.log('Update local user profile error:', error);
    return { success: false, error: 'Failed to update user profile.' };
  }
};

// Reset password (mock function)
const resetLocalPassword = async (email) => {
  try {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const normalizedEmail = email.trim().toLowerCase();
    
    if (!DEMO_USERS[normalizedEmail]) {
      return { success: false, error: 'Email address not found in our system.' };
    }

    console.log(`📧 Password reset email sent to: ${normalizedEmail}`);
    
    return { success: true, message: 'Password reset instructions sent to your email.' };
  } catch (error) {
    console.log('Reset local password error:', error);
    return { success: false, error: 'Failed to send reset email.' };
  }
};

// Auth state change listener (mock)
const onLocalAuthStateChange = (callback) => {
  // Since we're using localStorage, we can listen for storage events
  const handleStorageChange = (e) => {
    if (e.key === LOCAL_STORAGE_KEY) {
      const storedSession = getStoredSession();
      if (storedSession) {
        callback('SIGNED_IN', storedSession.session);
      } else {
        callback('SIGNED_OUT', null);
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  // Return subscription object
  return {
    data: {
      subscription: {
        unsubscribe: () => {
          window.removeEventListener('storage', handleStorageChange);
        }
      }
    }
  };
};

// Get available demo credentials (for UI display)
const getDemoCredentials = () => {
  return Object.keys(DEMO_USERS).map(email => ({
    email,
    password: DEMO_USERS[email].password,
    role: DEMO_USERS[email].profile.role,
    name: DEMO_USERS[email].profile.full_name,
    department: DEMO_USERS[email].profile.department
  }));
};

// Validate user permissions
const hasPermission = (userProfile, permission) => {
  if (!userProfile || !userProfile.permissions) return false;
  return userProfile.permissions.includes('all') || userProfile.permissions.includes(permission);
};

// Export local authentication service
export default {
  signIn: localSignIn,
  signOut: localSignOut,
  getSession: getLocalSession,
  getUserProfile: getLocalUserProfile,
  updateUserProfile: updateLocalUserProfile,
  resetPassword: resetLocalPassword,
  onAuthStateChange: onLocalAuthStateChange,
  getDemoCredentials,
  hasPermission
};